import React, { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import SoftAvatar from "components/SoftAvatar";
import SoftButton from "components/SoftButton";
import { ApiResponse } from "model/apiResponseModel";
import api from "api/api";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import SoftBadge from "components/SoftBadge";
import { Link } from "react-router-dom";
import SearchField from "components/SearchField";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ConfirmDialog from "layouts/leaderboard/components/ConfirmDialog";
import DeleteTableButton from "components/DeleteTableButton";

export default function Rewards() {
  const location = useLocation();
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [allRewards, setAllRewards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [deleteRewardId, setDeleteRewardId] = useState("");
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const isAddOrEdit =
    location.pathname.startsWith("/rewards/add") ||
    location.pathname.startsWith("/rewards/edit");

  const filteredRewards = useMemo(() => {
    if (!searchQuery.trim()) return allRewards;

    const query = searchQuery.trim().toLowerCase();
    return allRewards.filter(
      (item) =>
        item.name?.toLowerCase().includes(query) ||
        item.key?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query)
    );
  }, [allRewards, searchQuery]);

  const buildRows = (rewards) =>
    rewards.map((item) => {
      const rewardIcon = item.icon || item.main_icon;

      return {
      linkPath: `/rewards`,
      title: (
        <SoftBox display="flex" alignItems="center" px={1} py={0.5}>
          {rewardIcon && (
            <SoftAvatar
              src={rewardIcon}
              alt={item.name}
              size="sm"
              variant=""
              sx={{
                "& img": {
                  objectFit: "cover",
                  width: "25px",  
                },
              }}
            />
          )}
          <SoftBox
            display="flex"
            flexDirection="column"
            ml={rewardIcon ? 1.5 : 0}
          >
            <SoftTypography variant="button" fontWeight="medium">
              {item.name}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      ),
      description: (
        <SoftTypography
          variant="caption"
          color="secondary"
          fontWeight="medium"
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            minHeight: "40px",
            maxWidth: "250px",
          }}
        >
          {item.description || "_"}
        </SoftTypography>
      ),
      category: (
        <SoftTypography
          variant="caption"
          color="secondary"
          fontWeight="medium"
          textTransform="capitalize"
        >
          {item.category || "_"}
        </SoftTypography>
      ),
      issuance: (
        <SoftBadge
          variant="gradient"
          badgeContent={item.issuance_mode === "auto" ? "Auto" : "Manual"}
          color={item.issuance_mode === "auto" ? "info" : "secondary"}
          size="xs"
        />
      ),
      status: (
        <SoftBadge
          variant="gradient"
          badgeContent={item.is_enabled ? "Enabled" : "Disabled"}
          color={item.is_enabled ? "success" : "warning"}
          size="xs"
        />
      ),
      actions: (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <SoftTypography
            component={Link}
            to={`/rewards/edit/?rewardId=${item?._id}`}
            variant="caption"
            color="secondary"
            fontWeight="medium"
            sx={{
              display: "flex",
              alignItems: "center",
              minHeight: "40px",
            }}
          >
            Edit
          </SoftTypography>
          <DeleteTableButton
            onClick={() => {
              setDeleteRewardId(item?._id);
              setIsOpenDeleteDialog(true);
            }}
          />
        </Box>
      ),
    };
    });

  const fetchRewards = async () => {
    try {
      setIsLoading(true);
      const response = await api.getRewards();
      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        setColumns([
          { name: "title", align: "left" },
          { name: "description", align: "center" },
          { name: "category", align: "center" },
          { name: "issuance", align: "center" },
          { name: "status", align: "center" },
          { name: "actions", align: "center" },
        ]);

        const raw = response.data?.rewards ?? response.data ?? [];
        const rewards = Array.isArray(raw) ? raw : [];
        setAllRewards(rewards);
        setRows(buildRows(rewards));
      } else {
        setAllRewards([]);
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching rewards:", error);
      setAllRewards([]);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loginData = JSON.parse(
      localStorage.getItem("loginData") || sessionStorage.getItem("loginData")
    );

    if (!loginData) {
      navigate("/authentication/sign-in");
    } else if (!isAddOrEdit) {
      fetchRewards();
    }
  }, [navigate, isAddOrEdit]);

  useEffect(() => {
    setRows(buildRows(filteredRewards));
  }, [filteredRewards]);

  const handleSearch = () => {
    setRows(buildRows(filteredRewards));
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleCloseDeleteDialog = () => {
    setIsOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await api.deleteReward({ rewardId: deleteRewardId });
      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        setIsOpenDeleteDialog(false);
        fetchRewards();
      } else {
        alert(apiResponse.message);
      }
    } catch (error) {
      console.error("Error deleting reward:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isAddOrEdit ? (
        <Outlet />
      ) : (
        <SoftBox py={3}>
          <SoftBox mb={3}>
            <Card>
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
              >
                <SoftTypography variant="h6" width="20%">
                  Rewards
                </SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={2}>
                  <SearchField
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                  />

                  <SoftButton
                    variant="gradient"
                    color="info"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/rewards/add")}
                  >
                    Add
                  </SoftButton>
                </SoftBox>
              </SoftBox>
              <SoftBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {isLoading ? (
                  <SoftBox
                    display="flex"
                    height="500px"
                    justifyContent="center"
                    alignItems="center"
                    p={3}
                  >
                    <SoftTypography variant="subtitle1" color="textSecondary">
                      Loading...
                    </SoftTypography>
                  </SoftBox>
                ) : rows.length > 0 ? (
                  <Table columns={columns} rows={rows} />
                ) : (
                  <SoftBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    p={3}
                  >
                    <SoftTypography variant="subtitle1" color="textSecondary">
                      No Rewards Data Found
                    </SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>
            </Card>
          </SoftBox>
        </SoftBox>
      )}
      <ConfirmDialog
        isOpen={isOpenDeleteDialog}
        handleClose={handleCloseDeleteDialog}
        handleConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title="Are you sure you want to delete this reward completely?"
        actionText="Yes, Delete"
      />
      <Footer />
    </DashboardLayout>
  );
}
